import { z } from 'zod';

/**
 * One recorded occurrence of a VARIABLE expense: what it cost on one day.
 *
 * `amount` is a string, not a number, and stays that way end to end. The
 * column is `numeric(12,2)`; Postgres and Drizzle both surface it as a decimal
 * string so cents survive exactly. Parsing it into a JS number here would
 * reintroduce the binary-float rounding the column type exists to avoid, and
 * `Number('')` silently yields `0` — a missing amount would render as a real
 * $0.00 charge (G15). Format for display, don't coerce for storage.
 *
 * `incurredOn` is the day the expense was INCURRED, not the day it was keyed
 * in. It decides which invoice the amount lands on.
 */
export const CustomerVariableExpenseOccurrenceSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  /** The `customer_expenses` line this belongs to. */
  customerExpenseId: z.string().uuid(),
  customerId: z.string().uuid(),
  incurredOn: z.string(),
  amount: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CustomerVariableExpenseOccurrence = z.infer<
  typeof CustomerVariableExpenseOccurrenceSchema
>;

/** Accepted on the wire as a number or a string. The API canonicalizes to a
 *  two-decimal string and REJECTS more than two decimal places rather than
 *  rounding — the same `costAmount` validator the catalog uses. */
const amount = z.union([z.number(), z.string()]);

/**
 * Record one occurrence against a line.
 *
 * There is no default for `incurredOn`, deliberately: a fee paid on 29
 * September and keyed in on 2 October belongs on September's invoice, and
 * defaulting to today would silently put it on October's.
 */
export const CreateOccurrenceInput = z
  .object({
    incurredOn: z.string(),
    amount,
  })
  .strict();
export type CreateOccurrenceInput = z.infer<typeof CreateOccurrenceInput>;

/**
 * Correct a recorded occurrence. Which LINE it belongs to is deliberately not
 * patchable — moving one between lines changes who is billed, with no trace
 * beyond a uuid swap in the audit diff. Delete it and record it against the
 * right line.
 */
export const PatchOccurrenceInput = z
  .object({
    incurredOn: z.string().optional(),
    amount: amount.optional(),
  })
  .strict();
export type PatchOccurrenceInput = z.infer<typeof PatchOccurrenceInput>;

/**
 * One row of the month grid: a variable line in force for the period, plus
 * whatever has been recorded against it inside that period.
 *
 * `occurrences` being empty is the state the whole screen exists to surface —
 * it is what the grid highlights and what `MISSING_VARIABLE_EXPENSE` flags on
 * the resulting invoice. It is NOT "zero", and must never render as $0.00.
 */
export interface VariableExpenseGridRow {
  lineId: string;
  customerId: string;
  customerName: string;
  /** The customer's external Client ID — the column the importer matches on. */
  customerQbId: string;
  expenseCatalogId: string;
  itemName: string;
  /** The line's own description. What tells two lines from the same catalog
   *  item apart, which is why it is shown even when it looks redundant. */
  description: string;
  occurrences: CustomerVariableExpenseOccurrence[];
}

/**
 * The grid's window, and the same one both downloads use.
 *
 * Half-open `[periodStart, periodEnd)`, matching how an invoice stores its
 * service period — so the grid and invoice generation cannot disagree about
 * which side of a month boundary a fee falls on.
 */
/** The grid's page envelope. `needsCount` is the whole period's outstanding
 *  count, independent of the page and of which tab asked — it is what the
 *  Needs data badge shows. */
export interface VariableExpenseGridMeta {
  total: number;
  limit: number;
  offset: number;
  needsCount: number;
}

/** One line's amount in a bulk save. */
export interface VariableExpenseBulkEntry {
  customerExpenseId: string;
  incurredOn: string;
  amount: string;
}

/**
 * What a bulk save did, row by row.
 *
 * `failed` is not an error case — a batch where one line's date fell outside
 * its own effective window still saved the rest, and the caller keeps those
 * rows on screen to retry rather than losing the whole entry.
 */
export interface VariableExpenseBulkResult {
  created: CustomerVariableExpenseOccurrence[];
  failed: Array<{ customerExpenseId: string; error: string }>;
}

export interface VariableExpenseGridQuery {
  /** Which tab. `needs` narrows to lines with no amount in the period; the
   *  server applies it in SQL, so the page and its total agree. */
  state?: 'all' | 'needs';
  periodStart: string;
  periodEnd: string;
  /** Narrow to one customer — what the customer-detail deep link uses. */
  customerId?: string;
  /** Narrow to one catalog item. Filters the LINE, so it narrows the
   *  needs-count as well as the page. */
  expenseCatalogId?: string;
  limit?: number;
  offset?: number;
}

/** Pagination for the per-line occurrence list. */
export interface OccurrenceListQuery {
  limit?: number;
  offset?: number;
}

export interface ImportOccurrenceRowError {
  code:
    | 'REQUIRED'
    | 'INVALID_FORMAT'
    | 'TOO_LONG'
    | 'NO_MATCHING_LINE'
    | 'AMBIGUOUS_LINE'
    | 'CONFLICT'
    | 'INTERNAL';
  message: string;
  field?: 'qbId' | 'customer' | 'item' | 'line' | 'date' | 'amount';
}

export interface ImportOccurrenceRow {
  rowNumber: number;
  outcome: 'created' | 'updated' | 'unchanged' | 'failed';
  errors?: ImportOccurrenceRowError[];
}

export interface ImportOccurrencesResult {
  totalRows: number;
  created: number;
  /** Always 0 — this importer only ever inserts. Present so the shape matches
   *  every other importer and the shared report panel's tile row is stable. */
  updated: number;
  /**
   * Rows that wrote nothing: either the Amount cell was blank (a template row
   * for a line with nothing incurred this period — the expected case, not an
   * error) or an identical (line, date, amount) row already existed, which is
   * what stops a re-imported file from doubling a customer's bill.
   */
  unchanged: number;
  failed: number;
  rows: ImportOccurrenceRow[];
}
